import React, { useEffect, useState } from "react";
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { object, mixed, string, date, boolean } from 'yup';
import { Container, Row, Col, Form } from "react-bootstrap";
import { sub } from 'date-fns';
import PropTypes from 'prop-types';
import ErrorHandler from '../../../Helpers/Errors/ErrorHandler';
import QuoteResult from '../Helpers/QuoteResults';
import BasicInfo from './BasicInfo';
import SuppData from '../../../Helpers/Data/Products/Supplemental/SuppData';
import Age from '../../../Helpers/Dates/Age';
import { SESSION_STORAGE_ACCOUNT_KEY, getSessionStorageItem } from '../../../../sessionStorage'
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined.js';

import '../../Quoter.scss';
import './Quoter.scss';

export default function Quoter(props) {

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: Quoter.ValidationSchema(props.ageSpecs),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const [loading, setLoading] = useState(true);
  const [quoteRan, setQuoteRan] = useState(false);
  const [quoteResults, setQuoteResults] = useState([]);
  const [selected, setSelected] = useState(false);

  const covrServicesUrl = process.env.REACT_APP_COVR_SERVICES_URL;

  const suppData = SuppData();

  const handleValueChange = (field, value) => {
    setValue(field, value);
    props.onBasicInfoFieldChange(field, value);
    triggerValidation(field);
  };

  const handleSelectedValueChange = (value) => {
    setValue('selected', value);
    setSelected(value);
  };

  const handleSelectedQuote = (value) => {
    var form = { ...props.fullFormState };
    form.pages.quoter.selectedQuote = value;
    props.onArrayChange(form);
    setValue('selectedQuote', value);
  }

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'gender' });
    setValue('gender', props.basicInfo.gender);
    register({ name: 'birthdate' });
    setValue('birthdate', props.basicInfo.birthdate);
    register({ name: 'state' });
    setValue('state', props.basicInfo.state);
    register({ name: 'usResident' });
    setValue('usResident', props.basicInfo.usResident);
    register({ name: 'selected' });
    setValue('selected', selected);
    register({ name: 'selectedQuote' });
    setValue('selectedQuote', props.formState.selectedQuote);

  }, []);

  const runQuote = (getIsSubscribed) => {
    setLoading(true);
    setQuoteRan(true);

    const age = Age(props.basicInfo.birthdate);

    fetch(`${covrServicesUrl}/api/Aflac/GetAflacQuotes?age=${age}&state=${props.basicInfo.state}`,
      {
        method: 'GET',
        headers: {
          'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
        }
      })
      .then(res => res.json())
      .then(data => {
        if (getIsSubscribed()) {
          setQuoteResults(data);
          setLoading(false);
        }
      }).catch((error) => {
        if (getIsSubscribed()) {
          ErrorHandler(error, setQuoteResults);
          setLoading(false);
        }
      });
  }

  function delay(t, v) {
    return new Promise(function (resolve) {
      setTimeout(resolve.bind(null, v), t)
    });
  }

  let debounce = null;
  useEffect(() => {
    setLoading(true);
    let isSubscribed1 = true;
    triggerValidation("birthdate").then(async () => {
      if (isSubscribed1) {
        await delay(2000);

        if ((Object.keys(errors).length >= 1 && !errors.selected) || !props.basicInfo.state || !props.basicInfo.birthdate || props.basicInfo.birthdate.indexOf('_') > -1) {
          if (quoteResults.length) {
            setQuoteResults([]);
          }
          return;
        }
        setLoading(true);
        let isSubscribed = true;
        if (debounce) {
          clearTimeout(debounce);
        }
        debounce = setTimeout(() => runQuote(() => isSubscribed), 1000);
        return () => {
          clearTimeout(debounce);
          isSubscribed = false;
        };
      }
    });

    return () => isSubscribed1 = false;

  }, [props.basicInfo.birthdate, props.basicInfo.state])

  return (
    <Container className="quoter aflac-quoter">
      <Form onSubmit={handleSubmit(props.onNext)}>
        <Row>
          <Col lg={4} className="mb-3">
            <BasicInfo
              formState={props.basicInfo}
              onFieldChange={(field, value) => props.onBasicInfoFieldChange(field, value)}
              hideBackNext={true}
              ageSpecs={suppData.specs.age}
              logoBottom
              noValidate
              errors={errors}
              handleValueChange={handleValueChange}
              register={register}
            />
          </Col>
          <Col lg={8}>
            <QuoteResult
              state={props.basicInfo.state}
              formState={props.formState}
              onFieldChange={props.onFieldChange}
              result={quoteResults}
              quoteRan={quoteRan}
              quoteResults={quoteResults}
              loading={loading}
              onNext={props.onNext}
              error={errors.selected}
              selected={selected}
              setSelected={handleSelectedValueChange}
              handleSelectedQuote={handleSelectedQuote}
            />
          </Col>
        </Row>
      </Form>
    </Container>

  )
}

Quoter.ValidationSchema = (ageSpecs) => {
  return object().shape({
    require: boolean(),
    gender: string().required('Gender is required.'),
    birthdate: date()
      .transform(emptyStringToUndefined)
      .typeError('Invalid date.')
      .max(sub(new Date(), { years: ageSpecs.min }), `Minimum age is ${ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.')
    ,
    state: string().required('State is required.'),
    usResident: mixed().oneOf([true], 'You must be a US citizen or permanent resident.'),
    selected: mixed().oneOf([true], 'Please select a product.'),
  })
};

Quoter.propTypes = {
  formState: PropTypes.object.isRequired,
  basicInfo: PropTypes.object.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onBasicInfoFieldChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  ageSpecs: PropTypes.object.isRequired,
}

Quoter.DefaultFormState = (prefill = false) => {
  return {
    specialNote: '',
    premium: null,
    carrierName: 'Aflac',
    term: 0,
    healthClass: '',
    product: 'Supplemental Insurance',
    selectedQuote: []
  }
}




// WEBPACK FOOTER //
// ./src/components/Quoters/Supplemental/steps/Quoter.js